import type { Table } from '@tanstack/react-table';
import { type ComponentRef, type MutableRefObject, useImperativeHandle, useMemo, useRef } from 'react';
import Input, { type InputProps } from '~/components2/forms/Input';
import InfoTooltip from '~/components2/InfoTooltip';
import {
  clearTimerRef,
  getSearchAsParams,
  SEARCH_INTERVAL,
  type SearchAsyncColumnDef,
  type SearchRef,
  type Timer,
} from './common';

interface SearchAsyncProps<T> extends Omit<InputProps, 'label'> {
  table: Table<T>;
  searchRef?: MutableRefObject<SearchRef | null>;
  columns: SearchAsyncColumnDef[];
  initialValue?: string;
  onCommit?: (value: string, params: { [key: string]: any }) => void | Promise<void>;
}

export type { SearchAsyncProps, SearchAsyncColumnDef, SearchRef };

export default function SearchAsync<T>({
  table,
  searchRef,
  columns,
  onCommit,
  initialValue = '',
  ...props
}: SearchAsyncProps<T>) {
  const inputRef = useRef<ComponentRef<'input'>>(null);
  const timerRef = useRef<Timer>(null);

  // expose "clear" method to the parent
  useImperativeHandle(searchRef, () => {
    return {
      clear: () => {
        clearTimerRef(timerRef.current);
        if (inputRef.current) {
          inputRef.current.value = '';
        }
      },
    };
  });

  const onChange = (e: string) => {
    clearTimerRef(timerRef.current);

    timerRef.current = setTimeout(() => {
      if (inputRef.current) {
        const value = inputRef.current.value;
        onCommit?.(value, getSearchAsParams({ value, columns }));
      }
    }, SEARCH_INTERVAL);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const tooltip = useMemo(() => {
    let hasDateColumn = false;

    return (
      <div className="space-y-2">
        <div>
          Supported fields:
          <br />
          <ul className="list-inside list-disc">
            {columns.map((c, i) => {
              if (c.type === 'date') {
                hasDateColumn = true;
              }

              const def = table.getColumn(c.id)?.columnDef.header;
              const t = `${def?.toString() || c.id}`;

              return (
                <li key={`search-column-${t}`} className="list-item">
                  {t}
                </li>
              );
            })}
          </ul>
        </div>

        <div>This search applies to all rows and overrides any existing filters.</div>

        {!!hasDateColumn && (
          <div>
            <span className="font-semibold">NOTE: </span> When searching by date, use "MM/DD/YYYY" format for best
            results.
          </div>
        )}
      </div>
    );
  }, [columns]);

  return (
    <div className="relative">
      <Input {...props} ref={inputRef} label="" placeholder="Search..." className="pl-8" onChange={onChange} />

      <div className="absolute left-3 top-1/2 -translate-y-1/2">
        <InfoTooltip text={tooltip} label="About search" />
      </div>
    </div>
  );
}
